package com.devi3east.bashbuddy

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.navigation.NavHostController
import androidx.navigation.compose.rememberNavController
import com.devi3east.bashbuddy.auth.ui.AuthUiEvent
import com.devi3east.bashbuddy.auth.util.RecoveryTokenStore
import com.devi3east.bashbuddy.auth.util.parseRecoveryToken
import com.devi3east.bashbuddy.common.service.ForceUpdateManager
import com.devi3east.bashbuddy.common.service.ForceUpdateScreen
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.features.app.data.rememberAppState
import com.devi3east.bashbuddy.features.app.navigation.AppNavHost
import com.devi3east.bashbuddy.features.common.ui.BashBuddyLoadingScreen
import com.devi3east.bashbuddy.features.common.util.UrlOpener
import com.devi3east.bashbuddy.features.common.util.dismissPlatformPreloader
import com.devi3east.bashbuddy.features.designSystem.theme.BashBuddyTheme
import com.devi3east.bashbuddy.preferences.AppPreferences
import kotlinx.coroutines.delay
import org.koin.compose.KoinContext
import org.koin.compose.koinInject

@Composable
fun App(
    navController: NavHostController = rememberNavController(),
    startDestination: String = Screen.EventsScreen.route,
    pendingDeepLink: MutableState<String?>? = null
) {
    /*
     * Keep the custom bash buddy loading screen visible
     * for at least a short minimum duration.
     *
     * The screen will remain visible longer if startup
     * work has not completed yet.
     */
    var minimumLoadingTimeElapsed by remember {
        mutableStateOf(false)
    }

    LaunchedEffect(Unit) {
        delay(1200)
        minimumLoadingTimeElapsed = true
    }

    BashBuddyTheme {
        KoinContext {
            val scope = rememberCoroutineScope()

            val appPreferences =
                koinInject<AppPreferences>()

            val urlOpener =
                koinInject<UrlOpener>()

            val appState = rememberAppState(
                navController = navController,
                scope = scope,
                appPreferences = appPreferences,
                urlOpener = urlOpener
            )

            val appConfigUiState by
            appState.appConfigViewModel.uiState.collectAsState()

            val authUiState by
            appState.authViewModel.uiState.collectAsState()

            val eventFeedUiState by
            appState.eventFeedViewModel.uiState.collectAsState()

            val requiresUpdate by
            ForceUpdateManager.requiresUpdate.collectAsState()

            // -------------------------------------------------
            // Startup Work
            // -------------------------------------------------

            /*
             * App configuration/version check and session
             * restoration can happen concurrently while the
             * custom loading screen is visible.
             */
            LaunchedEffect(Unit) {
                appState.appConfigViewModel.loadConfig()
            }

            LaunchedEffect(Unit) {
                appState.restoreTokenIfExists()
            }

            // -------------------------------------------------
            // Global Deep Link Receiver
            // -------------------------------------------------

            urlOpener.onRedirect = { fullUri ->
                pendingDeepLink?.value = fullUri
            }

            /*
             * The initial branded loading screen must remain
             * visible until:
             *
             * 1. The minimum display time has elapsed.
             * 2. App config/version checking has completed.
             *
             * We intentionally do NOT include the event feed
             * here because a force-update screen must not wait
             * for the event feed to load.
             */
            val initialStartupLoading =
                !minimumLoadingTimeElapsed ||
                        appConfigUiState.isLoading

            /*
            * Once the installed version is allowed to continue,
            * * keep the loading screen visible while authentication
            * * hydration finishes.
            * *
            * * Only wait for the initial event feed when the app is
            * * actually starting on the Events screen. Direct links
            * * such as /event/{id}/{slug} must not depend on the feed
            * * route being composed.
            * */
            val appContentLoading =
                authUiState.isHydrating ||
                        (
                                startDestination == Screen.EventsScreen.route &&
                                        !eventFeedUiState.hasCompletedInitialLoad
                                )

            val startupComplete =
                !initialStartupLoading &&
                        !requiresUpdate &&
                        !appContentLoading

            LaunchedEffect(startupComplete) {
                if (startupComplete) {
                    dismissPlatformPreloader()
                }
            }

            Box(
                modifier = Modifier.fillMaxSize()
            ) {

                // -------------------------------------------------
                // Normal Application
                // -------------------------------------------------

                /*
                 * Build the navigation graph once app configuration
                 * has completed and this client is allowed to run.
                 *
                 * The loading screen can remain on top while
                 * authentication and the event feed finish loading.
                 */
                if (
                    !appConfigUiState.isLoading &&
                    !requiresUpdate
                ) {

                    // ---------------------------------------------
                    // Auth Event Handling
                    // ---------------------------------------------

                    LaunchedEffect(Unit) {
                        appState.authViewModel.uiEvent.collect { event ->

                            when (event) {

                                // ---------- External ----------

                                is AuthUiEvent.OpenExternalSignIn -> {
                                    urlOpener.openUrl(event.url)
                                }

                                is AuthUiEvent.OpenExternalSignUp -> {
                                    urlOpener.openUrl(event.url)
                                }

                                // ---------- Internal navigation ----------

                                AuthUiEvent.NavigateToSignIn -> {
                                    navController.navigate(
                                        Screen.SignInScreen.route
                                    )
                                }

                                AuthUiEvent.NavigateToSignUp -> {
                                    navController.navigate(
                                        Screen.SignUpScreen.route
                                    )
                                }

                                // ---------- Auth success ----------

                                AuthUiEvent.NavigateToEventsScreen -> {
                                    val uiState =
                                        appState.authViewModel.uiState.value

                                    if (
                                        uiState.token != null &&
                                        uiState.profile != null
                                    ) {
                                        appState.setAuthData(
                                            token = uiState.token,
                                            profile = uiState.profile,
                                            organization = uiState.organization
                                        )

                                        navController.navigate(
                                            Screen.EventsScreen.route
                                        ) {
                                            popUpTo(0) {
                                                inclusive = true
                                            }

                                            launchSingleTop = true
                                        }
                                    }
                                }

                                // ---------- Sign Out ----------

                                AuthUiEvent.SignedOut -> {
                                    appState.onSignedOut()
                                }

                                else -> Unit
                            }
                        }
                    }

                    // ---------------------------------------------
                    // App Navigation
                    // ---------------------------------------------

                    AppNavHost(
                        appState = appState,
                        startDestination = startDestination
                    )

                    // ---------------------------------------------
                    // Deep Link Navigation Handling
                    // ---------------------------------------------

                    LaunchedEffect(pendingDeepLink?.value) {
                        val fullUri =
                            pendingDeepLink?.value
                                ?: return@LaunchedEffect

                        pendingDeepLink.value = null

                        val uriWithoutScheme =
                            fullUri.substringAfter("://", "")

                        val host =
                            uriWithoutScheme
                                .substringBefore("/")
                                .substringBefore("?")
                                .substringBefore("#")

                        val path =
                            uriWithoutScheme
                                .substringAfter("/")
                                .substringBefore("?")
                                .substringBefore("#")

                        when {

                            // -------------------------------------
                            // Password Reset
                            // -------------------------------------

                            host.contains(
                                "reset",
                                ignoreCase = true
                            ) ||
                                    path.contains(
                                        "reset",
                                        ignoreCase = true
                                    ) -> {

                                val errorCode =
                                    fullUri.substringAfter(
                                        "error_code=",
                                        ""
                                    )
                                        .substringBefore("&")
                                        .ifBlank {
                                            fullUri.substringAfter(
                                                "error=",
                                                ""
                                            ).substringBefore("&")
                                        }

                                if (errorCode.isNotBlank()) {
                                    RecoveryTokenStore.token = null

                                    navController.navigate(
                                        Screen.ResetPasswordScreen.route
                                    ) {
                                        launchSingleTop = true
                                    }

                                    return@LaunchedEffect
                                }

                                val recoveryToken =
                                    parseRecoveryToken(fullUri)

                                if (recoveryToken.isNotBlank()) {
                                    RecoveryTokenStore.token =
                                        recoveryToken
                                } else {
                                    RecoveryTokenStore.token = null
                                }

                                navController.navigate(
                                    Screen.ResetPasswordScreen.route
                                ) {
                                    launchSingleTop = true
                                }
                            }

                            // -------------------------------------
                            // Stripe Onboarding Success
                            // -------------------------------------

                            host.contains(
                                "stripe",
                                ignoreCase = true
                            ) &&
                                    path.contains(
                                        "onboarding/success",
                                        ignoreCase = true
                                    ) -> {

                                navController.navigate(
                                    Screen.EventsScreen.route
                                ) {
                                    popUpTo(
                                        Screen.EventsScreen.route
                                    ) {
                                        inclusive = true
                                    }

                                    launchSingleTop = true
                                }
                            }

                            // -------------------------------------
                            // Stripe Checkout Success
                            // -------------------------------------

                            host.contains(
                                "stripe",
                                ignoreCase = true
                            ) &&
                                    path.contains(
                                        "success",
                                        ignoreCase = true
                                    ) -> {

                                val sessionId =
                                    fullUri.substringAfter(
                                        "session_id=",
                                        ""
                                    ).substringBefore("&")

                                val orgId =
                                    fullUri.substringAfter(
                                        "org_id=",
                                        ""
                                    ).substringBefore("&")

                                if (
                                    sessionId.isNotBlank() &&
                                    orgId.isNotBlank()
                                ) {
                                    navController.navigate(
                                        Screen.StripeSuccessScreen
                                            .createRoute(
                                                sessionId,
                                                orgId
                                            )
                                    ) {
                                        launchSingleTop = true
                                    }
                                } else {
                                    navController.navigate(
                                        Screen.CartScreen.route
                                    )
                                }
                            }

                            // -------------------------------------
                            // Stripe Cancel
                            // -------------------------------------

                            host.contains(
                                "stripe",
                                ignoreCase = true
                            ) &&
                                    path.contains(
                                        "cancel",
                                        ignoreCase = true
                                    ) -> {

                                navController.navigate(
                                    Screen.EventsScreen.route
                                ) {
                                    popUpTo(
                                        Screen.EventsScreen.route
                                    ) {
                                        inclusive = true
                                    }

                                    launchSingleTop = true
                                }
                            }

                            // -------------------------------------
                            // Default
                            // -------------------------------------

                            else -> {
                                navController.navigate(
                                    Screen.EventsScreen.route
                                ) {
                                    launchSingleTop = true
                                }
                            }
                        }
                    }
                }

                // -------------------------------------------------
                // Startup / Force Update Overlay
                // -------------------------------------------------

                when {

                    /*
                     * Always show the branded screen for the
                     * minimum duration and while app config/version
                     * checking is still running.
                     */
                    initialStartupLoading -> {
                        BashBuddyLoadingScreen()
                    }

                    /*
                     * Version checking has completed.
                     *
                     * If this client is no longer supported,
                     * show the force-update screen immediately
                     * rather than waiting for session or feed data.
                     */
                    requiresUpdate -> {
                        ForceUpdateScreen()
                    }

                    /*
                     * The installed version is supported.
                     *
                     * Keep the branded loading screen visible
                     * while the saved session is hydrated and
                     * the first event feed request completes.
                     */
                    appContentLoading -> {
                        BashBuddyLoadingScreen()
                    }
                }
            }
        }
    }
}